import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { SITE_URL } from "@/lib/seo";
import { WORK } from "@/constants/testIds";

const projects = [
  {
    slug: "petal-and-paw",
    title: "Petal & Paw",
    tagline: "Pet-safe florals, beautifully online.",
    industry: "Florist · E-commerce",
    year: "2025",
    image: "/petalpaw-desktop-1.jpg",
  },
];

export default function Work() {
  const crumbs = [
    { name: "Home", url: `${SITE_URL}/` },
    { name: "Work", url: `${SITE_URL}/work` },
  ];
  return (
    <main className="pt-32 sm:pt-40 bg-lak-bg">
      <Seo
        title="Selected Work · Boutique Website Case Studies"
        description="A growing collection of bespoke websites designed and built by LAK Atelier for independent UK brands. Featuring the Petal & Paw case study."
        keywords="web design portfolio, boutique website case studies, bespoke website examples, editorial website design UK"
        breadcrumbs={crumbs}
      />
      {/* Header */}
      <section className="lak-section pb-20 sm:pb-32">
        <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
          <Breadcrumbs items={crumbs} className="mb-8" />
          <Reveal><span className="eyebrow">Selected work · A growing collection</span></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-8 font-serif text-6xl sm:text-7xl lg:text-9xl font-light tracking-tight leading-[0.96] max-w-5xl">
              Brands we&rsquo;ve helped <em className="italic text-lak-sage">elevate online.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl text-lg text-lak-soft">
              Every site we make is built one at a time, with the same care a luxury studio gives a flagship store.
              A small but considered collection, and a list growing slowly, by design.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Projects list */}
      <section data-testid={WORK.list} className="lak-section pb-32 border-t border-black/10">
        {projects.map((p, i) => (
          <Reveal key={`${p.title}-${i}`}>
            <article
              data-testid={p.slug ? WORK.projectCard : `${WORK.projectCard}-placeholder`}
              className={`border-b border-black/10 group ${p.slug ? "" : "opacity-60"}`}
            >
              <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 py-16 sm:py-24 grid grid-cols-12 gap-6 lg:gap-10 items-center">
                <div className="col-span-12 lg:col-span-5">
                  <span className="eyebrow">#{String(i+1).padStart(3,"0")} · {p.year}</span>
                  <h2 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02] group-hover:text-lak-sage transition-colors">
                    {p.title}
                  </h2>
                  <p className="mt-4 font-serif italic text-2xl text-lak-soft">{p.tagline}</p>
                  <p className="mt-4 eyebrow">{p.industry}</p>
                  {p.slug && (
                    <Link to={`/work/${p.slug}`} className="mt-10 inline-flex items-center gap-3 px-8 py-4 btn-outline text-[11px] tracking-[0.28em] uppercase">
                      View Case Study <ArrowUpRight size={16} strokeWidth={1.25} />
                    </Link>
                  )}
                </div>
                <div className="col-span-12 lg:col-span-7">
                  {p.image ? (
                    <Link to={`/work/${p.slug}`} className="block overflow-hidden bg-lak-secondary">
                      <img src={p.image} alt={p.title} className="w-full transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]" />
                    </Link>
                  ) : (
                    <div className="aspect-[4/3] bg-lak-secondary flex items-center justify-center">
                      <span className="font-serif italic text-3xl text-lak-soft">In atelier, coming soon</span>
                    </div>
                  )}
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </section>

      {/* CTA */}
      <section className="lak-section py-32 bg-lak-secondary">
        <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 text-center">
          <Reveal>
            <h3 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.05]">
              Your project could be <em className="italic text-lak-sage">next.</em>
            </h3>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/contact" className="mt-12 inline-flex items-center gap-3 px-10 py-5 btn-sage text-[11px] tracking-[0.28em] uppercase">
              Start Your Project <ArrowUpRight size={16} strokeWidth={1.25} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
